/* eslint-disable prettier/prettier */
import React, { useState, useCallback } from 'react';
import { Activity } from '../../types/schedule.types';
import { AttendanceStatus } from '../../services/coachingService';

import { useScheduleActivities } from '../../hooks/useScheduleActivities';
import { useActivityFilter } from '../../hooks/useActivityFilter';
import { useActivityTypes } from '../../hooks/useActivityType';
import { useCoaching } from '../../hooks/useCoaching';
import { useLessonAttendance } from '../../hooks/useLessonAttendance';
import PeriodNavigator from '../scheduler/PeriodNavigator';
import ActivityTypeFilter from './ActivityTypeFilter';
import WeekGrid from './WeekGrid';
import styles from '../../styles/Schedule/WeeklySchedule.module.css';
import AttendanceModal, {
	Participant,
} from '../pageComponents/dashboard/coach/AttendanceModal';
import ViewActivityModal from '../pageComponents/dashboard/coach/ViewActivityModal';
import Dropdown from '../controlVariations/DropdownProgram';
import MultiSelectDropdown from '../controlVariations/MultiSelectDropdownProgram';
import { useCourseEnrollment } from '../../hooks/useCourseEnrollment';
import ViewToggle from './ViewToggle';
import { useScheduler } from '../../hooks/useScheduler';
import MonthlySchedule from './MonthlySchedule';

const WeeklySchedule: React.FC = () => {
	const [selectedActivity, setSelectedActivity] = useState<Activity | null>(
		null
	);
	const [viewModalOpen, setViewModalOpen] = useState(false);

	const [openModal, setOpenModal] = useState(false);
	const [selectedActivityTypes, setSelectedActivityTypes] = useState<
		string[]
	>([]);
	const [participants, setParticipants] = useState<any[]>([]);
	// const [viewMode, setViewMode] = useState<'week' | 'month'>('week');

	const {
		lesson,
		loading: lessonLoading,
		getLessonDetail,
		submitAttendance,
		attendanceLoading,
	} = useLessonAttendance();

	const { activeFilters, toggleFilter } = useActivityFilter();

	const {
		state,
		activeRange,
		setViewMode,
		navigatePrev,
		navigateNext,
		openCalendar,
		closeCalendar,
		selectWeek,
		selectMonthRange,
		navigateMonth,
		setCalendarView,
		selectMonth,
		selectYear,
	} = useScheduler();

	const { activitiesByDate, loading } = useScheduleActivities(
		activeRange,
		activeFilters,
		state.viewMode
	);

	const { activityTypes } = useActivityTypes();

	React.useEffect(() => {
		Array.from(activeFilters).forEach(filter => {
			toggleFilter(filter);
		});

		selectedActivityTypes.forEach(type => {
			toggleFilter(type as any);
		});
	}, [selectedActivityTypes]);

	const activityDropdownOptions = [
		{
			label: 'All Activities',
			value: 'all',
			color: '#64748B',
		},

		...(activityTypes?.map((item: any) => ({
			label: item.title,
			value: item.title,
			color: item.color,
		})) || []),
	];

	const handleActivityClick = useCallback(
		async (activity: Activity) => {
			try {
				setSelectedActivity(activity);

				await getLessonDetail(activity.id);

				const now = new Date();

				// activity end time
				const activityEndTime = new Date(
					activity.endTime || activity.date || ''
				);

				if (activityEndTime <= now) {
					setOpenModal(true);
				} else {
					setViewModalOpen(true);
				}
			} catch (error) {
				console.log('Error loading lesson detail', error);
			}
		},
		[getLessonDetail]
	);

	const handleCloseModal = () => {
		setOpenModal(false);
		setViewModalOpen(false);
		setSelectedActivity(null);
	};

	const formatTimeRange = (start: string, end: string) => {
		const startDate = new Date(start);
		const endDate = new Date(end);

		const formatTime = (date: Date) =>
			date.toLocaleTimeString('en-IN', {
				hour: '2-digit',
				minute: '2-digit',
				hour12: false,
				timeZone: 'Asia/Kolkata',
			});

		return `${formatTime(startDate)} to ${formatTime(endDate)} IST`;
	};

	const formatDate = (dateString: string) => {
	if (!dateString) return '';

	return new Date(dateString).toLocaleDateString('en-GB', {
		day: '2-digit',
		month: 'short',
		year: 'numeric',
		timeZone: 'Asia/Kolkata',
	});
};

	const isOneToOneMentoring =
		selectedActivity?.activitytype === 'One to One Mentoring';

	const attendanceAlreadyMarked =
		lesson?.participants?.some(
			p =>
				p.attendancestatus === 'attended' ||
				p.attendancestatus === 'absent' ||
				p.attendancestatus === 'late' ||
				p.attendancestatus === 'excused'
		) || false;

	const mappedParticipants: Participant[] =
		lesson?.participants?.map(participant => {
			let status: 'present' | 'absent' | 'late' | 'excused' | null = null;

			switch (participant.attendancestatus) {
				case 'attended':
					status = 'present';
					break;

				case 'late':
					status = 'late';
					break;

				case 'excused':
					status = 'excused';
					break;

				case 'absent':
					status = 'absent';
					break;

				default:
					status = null;
			}

			return {
				id: participant.participantid,

				userid: participant.userid,

				name: participant.fullname,

				role:
					// participant.designation ||
					'Learner',

				status,

				email: participant.email,

				avatar: participant.profilepicture || undefined,
			};
		}) || [];
	return (
		<div className={styles.container}>
			{/* Top bar */}
			<div className={styles.topBar}>
				<div className={styles.leftControls}>
					<PeriodNavigator
						viewMode={state.viewMode}
						selectedWeek={state.selectedWeek}
						selectedMonth={state.selectedMonth}
						currentMonth={state.currentMonth}
						currentYear={state.currentYear}
						isCalendarOpen={state.isCalendarOpen}
						calendarView={state.calendarView}
						onPrev={navigatePrev}
						onNext={navigateNext}
						onOpenCalendar={openCalendar}
						onCloseCalendar={closeCalendar}
						onSelectWeek={selectWeek}
						onSelectMonthRange={selectMonthRange}
						onNavigateMonth={navigateMonth}
						onSetCalendarView={setCalendarView}
						onSelectMonth={selectMonth}
						onSelectYear={selectYear}
					/>

					<ViewToggle value={state.viewMode} onChange={setViewMode} />
				</div>

				<div className={styles.filterSection}>
					<label className={styles.dropdownLabel}>
						Activity Type :
					</label>
					<MultiSelectDropdown
						placeholder="Select Activity Type"
						selectedLabel="Activity Type"
						options={activityDropdownOptions.filter(
							item => item.value !== 'all'
						)}
						selectedValues={selectedActivityTypes}
						onChange={values => {
							setSelectedActivityTypes(values);
						}}
						width="332px"
						height="48px"
						menuMaxHeight="220px"
					/>
				</div>
			</div>

			<div className={styles.divider} />

			{state.viewMode === 'week' ? (
				<WeekGrid
					selectedWeek={state.selectedWeek}
					activitiesByDate={activitiesByDate}
					loading={loading}
					onActivityClick={handleActivityClick}
				/>
			) : (
				<MonthlySchedule
					selectedMonth={state.selectedMonth}
					activitiesByDate={activitiesByDate}
					loading={loading}
				/>
			)}

			{/* Attendance Modal */}

			<AttendanceModal
				open={openModal}
				onClose={handleCloseModal}
				onEdit={() => console.log('Edit clicked')}
				onSave={async updatedParticipants => {
					try {
						if (!lesson?.id) return;

						const payload: {
							userid: string;
							attendancestatus:
								| 'attended'
								| 'late'
								| 'excused'
								| 'absent';
						}[] = updatedParticipants.map(participant => ({
							userid: participant.userid || '',

							attendancestatus:
								participant.status === 'present'
									? 'attended'
									: participant.status === 'late'
										? 'late'
										: participant.status === 'excused'
											? 'excused'
											: 'absent',
						}));
						await submitAttendance(lesson.id, payload);

						console.log('Attendance submitted successfully');
					} catch (error) {
						console.log('Failed to submit attendance', error);
					}
				}}
				title={lesson?.title || ''}
				activityType={lesson?.activitytype?.title || ''}
				activityTypeColor={lesson?.activitytype?.color || '#EF4444'}
				date={formatDate(lesson?.scheduledstarttime || '')}
				time={formatTimeRange(
					lesson?.scheduledstarttime || '',
					lesson?.scheduledendtime || ''
				)}
				programName={lesson?.course?.title || ''}
				masterclassTitle={lesson?.title || ''}
				description={lesson?.description || ''}
				participants={mappedParticipants}
				attendanceAlreadyMarked={attendanceAlreadyMarked}
			/>

			<ViewActivityModal
				open={viewModalOpen}
				onClose={handleCloseModal}
				title={lesson?.title || ''}
				activityType={lesson?.activitytype?.title || ''}
				activityTypeColor={lesson?.activitytype?.color || '#EF4444'}
				date={formatDate(lesson?.scheduledstarttime || '')}
				time={formatTimeRange(
					lesson?.scheduledstarttime || '',
					lesson?.scheduledendtime || ''
				)}
				programName={lesson?.course?.title || ''}
				masterclassTitle={lesson?.title || ''}
				description={lesson?.description || ''}
				participants={mappedParticipants}
			/>
		</div>
	);
};

export default WeeklySchedule;
